class CollectionLoadMore extends HTMLElement {
  constructor() {
    super();
    this.observer = null;
    this.isLoading = false;
    this.hasMore = true;
    this._hasConnected = false;
    this._boundLoadMore = this.loadMore.bind(this);
  }

  connectedCallback() {
    if (this._hasConnected) return;

    this.button = this.querySelector('button');
    this.grid = this.closest('.collection-section')?.querySelector('.collection-grid') || document.querySelector('.collection-grid');
    
    if (!this.button || !this.grid) return;
    this._hasConnected = true;
    
    if (this.dataset.scroll === 'true') {
      this.initIntersectionObserver();
    } else {
      this.button.addEventListener('click', this._boundLoadMore);
    }
  }

  initIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '0px 0px 500px 0px',
      threshold: 0
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.loadMore();
        }
      });
    }, options);

    this.observer.observe(this);
  }

  async loadMore(event) {
    event?.preventDefault();

    if (this.isLoading || !this.hasMore) return;

    const nextUrl = this.button.dataset.href;
    if (!nextUrl) {
      this.finishPagination();
      return;
    }

    this.setLoading(true);
    
    try {
      const response = await fetch(nextUrl, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Failed to load products: ${response.status}`);

      const text = await response.text();
      const parser = new DOMParser();
      const html = parser.parseFromString(text, 'text/html');
      
      const newProducts = this.getNextProducts(html);
      const nextButton = html.querySelector('.load-more-btn');

      if (newProducts.length === 0) {
        if (!nextButton) {
          this.finishPagination();
          return;
        }
        throw new Error('No products found in the next page markup');
      }
      
      const fragment = document.createDocumentFragment();
      newProducts.forEach(product => {
        fragment.appendChild(document.importNode(product, true));
      });
      this.grid.appendChild(fragment);

      if (nextButton && nextButton.dataset.href) {
        this.button.dataset.href = nextButton.dataset.href;
        this.setLoading(false);
        this.queueAutoLoadIfNeeded();
      } else {
        this.finishPagination();
      }

    } catch (error) {
      console.error('Error loading products:', error);
      this.setLoading(false);
    }
  }

  getNextProducts(html) {
    const nextGrid = html.querySelector('#product-grid');
    if (nextGrid) {
      return Array.from(nextGrid.children).filter(element => (
        element.matches('product-card, .product-card, .product-list__grid-item')
      ));
    }

    return Array.from(html.querySelectorAll('product-card, .product-card, .product-list__grid-item'));
  }

  setLoading(isLoading) {
    this.isLoading = isLoading;
    this.button.toggleAttribute('disabled', isLoading);
    this.button.classList.toggle('loading', isLoading);
  }

  finishPagination() {
    this.hasMore = false;
    this.setLoading(false);
    this.observer?.disconnect();
    this.style.display = 'none';
  }

  queueAutoLoadIfNeeded() {
    if (this.dataset.scroll !== 'true' || !this.hasMore) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (this.isTriggerNearViewport()) {
          this.loadMore();
        }
      });
    });
  }

  isTriggerNearViewport() {
    const preloadDistance = 500;
    const rect = this.getBoundingClientRect();

    return rect.top <= window.innerHeight + preloadDistance && rect.bottom >= -preloadDistance;
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.button?.removeEventListener('click', this._boundLoadMore);
    this.observer = null;
    this._hasConnected = false;
  }
}

if (!customElements.get('collection-load-more')) {
  customElements.define('collection-load-more', CollectionLoadMore);
}

// Collection Description Read More
class CollectionDescription extends HTMLElement {
  constructor() {
    super();
    this._hasConnected = false;
    this._boundToggle = this.toggle.bind(this);
  }

  connectedCallback() {
    if (this._hasConnected) return;
    this._hasConnected = true;

    this.readMoreBtn = this.querySelector('[data-read-more]');
    if (!this.readMoreBtn) return;
    
    this.shortDescription = this.querySelector('[data-description-short]');
    this.fullDescription = this.querySelector('[data-description-full]');
    this.readMoreText = this.readMoreBtn.querySelector('[data-read-more-text]');
    
    this.readMoreBtn.addEventListener('click', this._boundToggle);
  }
  
  toggle() {
    const isExpanded = this.readMoreBtn.getAttribute('aria-expanded') === 'true';
    
    if (isExpanded) {
      this.shortDescription.style.display = '';
      this.fullDescription.style.display = 'none';
      this.readMoreText.textContent = 'Read more';
      this.readMoreBtn.setAttribute('aria-expanded', 'false');
    } else {
      this.shortDescription.style.display = 'none';
      this.fullDescription.style.display = 'block';
      this.readMoreText.textContent = 'Read less';
      this.readMoreBtn.setAttribute('aria-expanded', 'true');
    }
  }
}

if (!customElements.get('collection-description')) {
  customElements.define('collection-description', CollectionDescription);
}
